import { Catalog, CatalogProvider, ShopStorefrontProvider } from '@apps/registry/common/components/Catalog';
import React from 'react';
import { useShopController } from './Shop.controller';

import '@assets/charm.css';
import { HeroContent } from './components';

import { AddCustomGiftDialogProvider } from '@apps/registry/common/components/AddCustomGiftDialog/AddCustomGiftDialog.provider';
import { ShippingAddressAndCurrencyDialogsProvider } from '@apps/registry/common/components/ShippingAddressAndCurrencyDialogsProvider';
import type { TrackAttemptFn, UseAddItemToRegistryAttemptReturn } from '@apps/registry/public/routes/Shop/useAddItemToRegistryAttempt';
import type { ShippingAddressFragment } from '@graphql/aliases';
import type { RegistryCurrencyCodesFragment } from '@graphql/fragments/currency.graphql.generated';
import type { EventType } from '@graphql/generated-base';
import { Container } from './Shop.styles';
import { PublicShopTelemetryProvider } from './Shop.telemetry';
import { TopNav } from './components/TopNav';
import { CreditCardSetupProvider } from '@apps/registry/admin/components/CreditCardSetup/CreditCardSetup.provider';

interface ShopProps {}

interface RootProps {
  eventId: string | undefined;
  eventType: Maybe<EventType>;
  eventHandle: string | null;
  handleOnAddItemToRegistryWithoutEvent?: TrackAttemptFn;
  addToRegistryAttemptInfo?: UseAddItemToRegistryAttemptReturn['addToRegistryAttemptInfo'];
  showEventPickerDialog: () => void;
  removeAddAttemptInfo?: () => void;
  shippingAddress: ShippingAddressFragment | null;
  registryCurrency: RegistryCurrencyCodesFragment | null;
}

const Inner: React.FC<RootProps> = ({
  eventId,
  eventType,
  eventHandle,
  handleOnAddItemToRegistryWithoutEvent,
  addToRegistryAttemptInfo,
  removeAddAttemptInfo,
  shippingAddress,
  showEventPickerDialog,
  registryCurrency,
}) => {
  return (
    <Container>
      <CatalogProvider
        registryCurrency={registryCurrency}
        eventId={eventId}
        eventType={eventType}
        onAddItemToRegistryWithoutEvent={handleOnAddItemToRegistryWithoutEvent}
        addToRegistryAttemptInfo={addToRegistryAttemptInfo}
        removeAddAttemptInfo={removeAddAttemptInfo}
        registryShippingAddress={shippingAddress}
        eventHandle={eventHandle}
      >
        <Catalog
          header={<TopNav showEventPickerDialog={showEventPickerDialog} />}
          heroContent={<HeroContent />}
        />
      </CatalogProvider>
    </Container>
  );
};

const Root: React.FC<ShopProps> = () => {
  const {
    shippingAddress,
    eventId,
    eventType,
    handleOnAddItemToRegistryWithoutEvent,
    addToRegistryAttemptInfo,
    removeAddAttemptInfo,
    showEventPickerDialog,
    registryCurrency,
    website,
    orders,
    finalizedEventDate,
    registries,
  } = useShopController();
  const content = (
    <AddCustomGiftDialogProvider
      registryCurrency={registryCurrency}
      eventId={eventId}
    >
      <ShippingAddressAndCurrencyDialogsProvider
        eventId={eventId || ''}
        registryCurrency={registryCurrency}
        registryShippingAddress={shippingAddress}
        website={website}
        finalizedEventDate={finalizedEventDate}
        orders={orders}
        registries={registries}
      >
        <Inner
          eventId={eventId}
          eventType={eventType}
          shippingAddress={shippingAddress}
          handleOnAddItemToRegistryWithoutEvent={handleOnAddItemToRegistryWithoutEvent}
          addToRegistryAttemptInfo={addToRegistryAttemptInfo}
          removeAddAttemptInfo={removeAddAttemptInfo}
          showEventPickerDialog={showEventPickerDialog}
          registryCurrency={registryCurrency}
          eventHandle={website}
        />
      </ShippingAddressAndCurrencyDialogsProvider>
    </AddCustomGiftDialogProvider>
  );

  return (
    <div>
      {eventId ? (
        <CreditCardSetupProvider
          eventId={eventId}
          registryShippingAddress={shippingAddress}
        >
          {content}
        </CreditCardSetupProvider>
      ) : (
        content
      )}
    </div>
  );
};

const Shop = (props: ShopProps) => {
  return (
    <PublicShopTelemetryProvider>
      {/* Not passing storefront to `ShopStorefrontProvider` so that it can handle deriving the storefront */}
      <ShopStorefrontProvider>
        <Root {...props} />
      </ShopStorefrontProvider>
    </PublicShopTelemetryProvider>
  );
};

export default Shop;
